import { useEffect, type ReactNode } from 'react';
import { Stack } from '@mui/material';
import { useFormContext } from 'react-hook-form';

import type { AddressFieldConfig } from '../../config';
import type { SectionKey } from '../../lease-details.types';
import { FieldHelperText } from '../../components/field-helper.text';
import { LeaseAddressInput } from '../../inputs/lease-address-input';
import { useFieldEventContextBuilder } from '../use-field-event-context';
import { firstErrorMessage } from '../utils';
import {
  AddressesAutocomplete,
  formatAddressListValue,
  useAddresses,
} from '@/components/AddressesAutocomplete';
import type { AddressAutocompleteOption } from '@/utils/types/addressAutocompleteOption.interface';

export type AddressFieldProps = {
  path: string;
  field: AddressFieldConfig;
  sectionKey: SectionKey;
  arrayIndex?: number;
  required: boolean;
  disabled: boolean;
  label: ReactNode;
  helperText?: ReactNode;
};

export const AddressField = ({
  path,
  field,
  sectionKey,
  arrayIndex,
  required,
  disabled,
  label,
  helperText,
}: AddressFieldProps) => {
  const buildContext = useFieldEventContextBuilder<unknown>(path, sectionKey, arrayIndex);

  if (!field.linking) {
    return (
      <LeaseAddressInput
        name={path}
        label={typeof label === 'string' ? label : undefined}
        required={required}
        disabled={disabled}
        showLoadingSkeleton={field.showLoadingSkeleton}
        onSelect={
          field.events?.onChange
            ? (option) => field.events?.onChange?.(buildContext(option))
            : undefined
        }
      />
    );
  }

  return (
    <LinkedAddressField
      path={path}
      linking={field.linking}
      showLoadingSkeleton={field.showLoadingSkeleton}
      label={label}
      required={required}
      disabled={disabled}
      helperText={helperText}
      onSelect={
        field.events?.onChange
          ? (option) => field.events?.onChange?.(buildContext(option))
          : undefined
      }
    />
  );
};

type LinkedAddressFieldProps = {
  path: string;
  linking: NonNullable<AddressFieldConfig['linking']>;
  showLoadingSkeleton?: boolean;
  label: ReactNode;
  required: boolean;
  disabled: boolean;
  helperText?: ReactNode;
  onSelect?: (option: AddressAutocompleteOption | null) => void;
};

const LinkedAddressField = ({
  path,
  linking,
  showLoadingSkeleton,
  label,
  required,
  disabled,
  helperText: staticHelperText,
  onSelect,
}: LinkedAddressFieldProps) => {
  const { watch, setValue, getValues, trigger, getFieldState, formState } = useFormContext();
  const { addresses } = useAddresses();

  const buildingId = watch(linking.buildingIdField) as number | undefined;
  const propertyId = linking.propertyIdField
    ? (watch(linking.propertyIdField) as string | undefined)
    : undefined;

  useEffect(() => {
    if (
      !linking.hydratePropertyIdFromAddresses ||
      !linking.propertyIdField ||
      propertyId ||
      !buildingId ||
      !addresses
    ) {
      return;
    }

    const matched = addresses.find((address) => address.monolith_building_id === buildingId);
    if (matched?.property_id) {
      setValue(linking.propertyIdField, matched.property_id);
    }
  }, [
    addresses,
    buildingId,
    linking.hydratePropertyIdFromAddresses,
    linking.propertyIdField,
    propertyId,
    setValue,
  ]);

  const errorFieldNames = linking.errorFieldNames?.length ? linking.errorFieldNames : [path];
  const errorMessage =
    errorFieldNames
      .map((fieldName) => firstErrorMessage(getFieldState(fieldName, formState).error))
      .find(Boolean) ?? '';
  const hasError = errorMessage.length > 0;

  const resetFieldValues = (values: Record<string, unknown> | undefined) => {
    if (!values) return;
    for (const [fieldName, value] of Object.entries(values)) {
      setValue(fieldName, value);
    }
  };

  const handleChange = (addressOption: AddressAutocompleteOption | null) => {
    onSelect?.(addressOption);
    if (!addressOption) {
      setValue(linking.buildingIdField, linking.clearSelection?.restoreBuildingId ?? 0);
      setValue(path, linking.clearSelection?.restoreAddress ?? '');
      if (linking.propertyIdField) {
        setValue(linking.propertyIdField, '');
      }
      resetFieldValues(linking.clearSelection?.resetFields);
      return;
    }

    const isBuildingChange =
      addressOption.monolith_building_id !== getValues(linking.buildingIdField);
    setValue(linking.buildingIdField, addressOption.monolith_building_id);
    setValue(path, formatAddressListValue(addressOption));
    if (linking.propertyIdField) {
      setValue(linking.propertyIdField, addressOption.property_id ?? '');
    }
    if (isBuildingChange) {
      resetFieldValues(linking.resetFieldsOnBuildingChange);
    }
  };

  const onBlurFields = linking.validateOnBlurFields?.length ? linking.validateOnBlurFields : [path];

  return (
    <Stack direction="column" sx={{ width: '100%' }}>
      <AddressesAutocomplete
        data-testid={path}
        name={path}
        color="secondary"
        label={typeof label === 'string' ? label : undefined}
        required={required}
        disabled={disabled}
        value={buildingId ?? 0}
        showLoadingSkeleton={showLoadingSkeleton}
        error={hasError}
        helperText={errorMessage}
        onBlur={() => {
          void trigger([...onBlurFields]);
        }}
        onChange={handleChange}
      />
      {!hasError && staticHelperText != null && staticHelperText !== '' && (
        <FieldHelperText text={staticHelperText} disabled={disabled} />
      )}
    </Stack>
  );
};
